import React, { FC } from "react";
import type { ContentfulCallToActionType, ContentfulContactModalType, ParsedContentNode } from "@/types";
import { FormText } from "../form";
import { ContentManager } from "../content-manager";

type FormInput = {
  value: string | boolean;
  errorMessage?: string;
  fieldName: string;
};

type FormState = {
  [key: string]: FormInput; 
};

export const WhitePaperDownloadForm: FC<{
  onSubmit: (event: React.FormEvent) => void;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  isModal?: boolean;
  className?: string;
}> = ({ onSubmit, form, setForm, isModal, className }) => {
  return (
    <form className={`contactForm ${className || ""}`} onSubmit={onSubmit}>
      <div className="contactModal--subtitle">Get the Whitepaper</div>
      <div className="contactForm--fields">
        <FormText
          label="Full Name"
          value={form.name.value as string}
          required
          type="text"
          inputAttrs={{
            id: "input_7",
            name: "q7_fullName",
          }}
          errorMessage={form.name.errorMessage}
          onChange={(value) => {
            setForm((form) => ({
              ...form,
              name: {
                value,
                errorMessage: "",
                fieldName: "q7_fullName",
              },
            }));
          }}
        />
        <FormText
          label="Company Email"
          value={form.email.value as string}
          required
          type="email"
          inputAttrs={{
            id: "input_4",
            name: "q4_email",
          }}
          errorMessage={form.email.errorMessage}
          onChange={(value) => {
            setForm((form) => ({
              ...form,
              email: {
                value,
                errorMessage: "",
                fieldName: "q4_email",
              },
            }));
          }}
        />
        <FormText
          label="Phone Number"
          value={form.phone.value as string}
          type="tel"
          inputAttrs={{
            id: "input_5_full",
            name: "q5_phoneNumber[full]",
          }}
          onChange={(value) => {
            setForm((form) => ({
              ...form,
              phone: {
                value,
                errorMessage: "",
                fieldName: "q5_phoneNumber[full]",
              },
            }));
          }}
        />
        <FormText
          label="Company"
          value={form.company.value as string}
          type="text"
          inputAttrs={{
            id: "input_8",
            name: "q8_company",
          }}
          errorMessage={form.company.errorMessage}
          onChange={(value) => {
            setForm((form) => ({
              ...form,
              company: {
                value,
                errorMessage: "",
                fieldName: "q8_company",
              },
            }));
          }}
        />
        <button
          type="submit"
          className="link link_full link_blue contactForm--submit"
          data-title="Submit"
        >
          <span>Submit</span>
        </button>
      </div>
    </form>
  );
};

export const WhitePaperDownloadModal: FC<
  ContentfulContactModalType & 
  ContentfulCallToActionType & {
    onClose: () => void;
    sourcePage: string;
  }
> = ({ title, subtitle, content, buttonFile, onClose, sourcePage }) => {
  const [formSuccess, setFormSuccess] = React.useState<boolean | null>(null);
  const [form, setForm] = React.useState<{
    [key: string]: FormInput;
  }>({
    name: {
      value: "",
      errorMessage: "",
      fieldName: "q7_fullName",
    },
    email: {
      value: "",
      errorMessage: "",
      fieldName: "q4_email",
    },
    phone: {
      value: "",
      fieldName: "q5_phoneNumber[full]",
    },
    company: {
      value: "",
      errorMessage: "",
      fieldName: "q8_company",
    },
    website: {
      value: "",
      errorMessage: "",
      fieldName: "q9_companyWebsite",
    },
    jobTitle: {
      value: "",
      errorMessage: "",
      fieldName: "q10_jobTitle",
    },
    country: {
      value: "",
      errorMessage: "",
      fieldName: "q12_country",
    },
    type: {
      value: "",
      errorMessage: "",
      fieldName: "q6_typeA",
    },
    interestedIn: {
      value: "",
      errorMessage: "",
      fieldName: "q13_imInterested",
    },
    receiveOtherCommunications: {
      value: false,
      errorMessage: "",
      fieldName: "q14_iAgree",
    },
  });

  const resetFormValues = () => {
    Object.keys(form).forEach((key) => {
      setForm((form) => ({
        ...form,
        [key]: {
          ...form[key],
          value: "",
        },
      }));
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const requiredFields = [
      "name",
      "email",
    ];

    const invalidFields = requiredFields.filter((field) => {
      if (!form[field].value) {
        setForm((form) => ({
          ...form,
          [field]: {
            ...form[field],
            errorMessage: "This field is required",
          },
        }));
        return true;
      }
      return false;
    });

    if (invalidFields.length > 0) {
      return;
    } else {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        formData.append(form[key].fieldName, JSON.stringify(form[key].value).replace(/"/g, ''));
      });
      formData.append("q19_inputSource", sourcePage);
      try {
        const response = await fetch(
          "https://eu-submit.jotform.com/submit/240845478033156",
          {
            method: "POST",
            body: formData,
          }
        );

        if (response.ok) {
          // Handle successful submission
          console.log("Form submitted successfully", buttonFile);
          const downloadPDF = async () => {
            setFormSuccess(true);
            resetFormValues();
            const response = await fetch(buttonFile?.file?.url ?? '');
            const blob = await response.blob();
  
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${buttonFile?.file?.fileName ?? 'Whitepaper'}.pdf`; 
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          
          downloadPDF();
          onClose();
        } else {
          setFormSuccess(false);
          console.error("Form submission failed");
        }
      } catch (error) {
        setFormSuccess(false);
        console.error("An error occurred during form submission:", error);
      } finally {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <section className="contactModal whitePaperDownloadModal">
      <div className="contactModal--inner">
        <div className="contactModal--top">
          <div className="contactModal--title">{title}</div>
          <button className="contactModal--close" onClick={onClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="contactModal--main">
          <div className="contactModal--text">
            <div className="contactModal--subtitle">{subtitle}</div>
            <ContentManager
              content={content}
              className="contactModal--content"
            />
          </div>
          {formSuccess === null && (
            <WhitePaperDownloadForm
              onSubmit={handleSubmit}
              form={form}
              setForm={setForm}
              isModal
              className="contactModal--form"
            />
          )}
          {formSuccess === true && (
            <div className="contactModal--success">
              Thank You!
              <br />
              <br />
              Your submission has been received.
            </div>
          )}
          {formSuccess === false && (
            <div className="contactModal--error">
              There was an error, please try again later.
            </div>
          )}
        </div>
        <div className="contactModal--footer">
          <button onClick={onClose} className="link link_outline link_blue" style={{ visibility: "hidden" }}>
            Cancel
          </button>
          {formSuccess === null && (
            <button onClick={handleSubmit} className="link link_full link_blue">
              Download
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
